import axios from 'axios';
import { InlineError, InlineErrorCode, userMessageForCode } from './errors';

// Use relative URL - the proxy in package.json will forward /api requests to localhost:5000
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// For streaming endpoints, we need to bypass the React dev server proxy
// because it buffers responses and breaks SSE streaming
const getStreamingApiUrl = () => {
  // If a direct API URL is configured, use it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // In development, connect directly to the backend to bypass proxy buffering
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:5001/api';
  }
  // In production, use relative URL
  return '/api';
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface User {
  userId: number;
  fullName: string;
  email: string;
  role: string;
  departmentId?: number | null;
  departmentName?: string | null;
  isActive: boolean;
  createdAt: string;
  lastLoginAt?: string;
  mustChangePassword?: boolean;
  // Usage aggregates (admin user list only)
  totalSessions?: number;
  totalMessages?: number;
  lastActivityAt?: string;
  lastUsedAgentId?: number;
  lastUsedAgentName?: string;
}

export interface UserAgentUsage {
  agentId: number;
  agentName: string;
  agentType: string;
  avatarColor: string;
  sessionCount: number;
  messageCount: number;
  hasAccess: boolean;
  firstUsedAt?: string;
  lastUsedAt?: string;
}

export interface Department {
  departmentId: number;
  name: string;
  description: string;
  managerUserId?: number | null;
  managerName?: string | null;
  isActive: boolean;
  createdAt: string;
  memberCount: number;
}

// ==================== Analytics Types ====================

export interface AnalyticsSummary {
  totalUsers: number;
  activeUsers: number;
  totalSessions: number;
  totalMessages: number;
  totalAgentMessages: number;
  avgMessagesPerSession: number;
  activeAgents: number;
  totalAgents: number;
  positiveFeedback: number;
  negativeFeedback: number;
  newUsers: number;
}

/** The comparable KPIs for the equal-length window before the selected one. */
export interface AnalyticsPeriodTotals {
  activeUsers: number;
  totalMessages: number;
  totalSessions: number;
  positiveFeedback: number;
  negativeFeedback: number;
  newUsers: number;
}

export interface AnalyticsTimeBucket {
  date: string; // yyyy-MM-dd, in the viewer's time zone (tzOffsetMinutes)
  messages: number;
  sessions: number;
  activeUsers: number;
  positive: number;
  negative: number;
}

export interface DepartmentUsage {
  departmentId: number | null;
  name: string;
  totalUsers: number;
  activeUsers: number;
  messages: number;
}

export interface AgentUsageStat {
  agentId: number;
  agentName: string;
  agentType: string;
  avatarColor: string;
  sessionCount: number;
  messageCount: number;
  uniqueUsers: number;
  isActive: boolean;
  lastUsedAt?: string;
}

export interface AgentFeedbackStat {
  agentId: number;
  agentName: string;
  avatarColor: string;
  positive: number;
  negative: number;
  total: number;
  positiveRatio: number;
}

export interface AnalyticsOverview {
  rangeDays: number;
  summary: AnalyticsSummary;
  activity: AnalyticsTimeBucket[];
  topAgents: AgentUsageStat[];
  agentFeedback: AgentFeedbackStat[];
  tzOffsetMinutes: number;
  previous: AnalyticsPeriodTotals;
  /** User messages, [weekday 0=Sunday][hour 0–23], in the viewer's time zone. */
  heatmap: number[][];
  departments: DepartmentUsage[];
  /** When the server computed this (it caches for ~60s). */
  generatedAt: string;
}

export interface NegativeFeedbackItem {
  messageId: number;
  sessionId: number;
  agentId?: number;
  agentName?: string;
  messageText: string;
  sentAt: string;
  feedbackAt: string;
  userName: string;
}

export interface LoginRequest {
  email: string;
  password: string;
  rememberMe: boolean;
}

export interface LoginResponse {
  token: string;
  refreshToken: string;
  user: User;
}

export interface ChatMessage {
  messageId: number;
  sessionId: number;
  senderType: string;
  messageText: string;
  sentAt: string;
  isRead: boolean;
  files?: FileInfo[];
  feedback?: 'positive' | 'negative' | null;
  editedAt?: string;
  originalText?: string;
  // Client-only UX metadata captured during streaming. Not persisted in the DB
  // today — populated when the agent reply lands in-session so the "Thought
  // for Xs · used N tools" chip renders above the saved bubble. Survives
  // within the session but not across refresh.
  thinkingDurationMs?: number;
  toolTimeline?: { id: number; name: string; startedAt: number; endedAt?: number }[];
}

export interface FeedbackResponse {
  feedbackId?: number;
  messageId: number;
  userId: number;
  feedbackType: 'positive' | 'negative' | null;
  createdAt: string;
  updatedAt?: string;
}

export interface FileInfo {
  fileId: number;
  fileName: string;
  fileType: string;
  fileSize: number;
  uploadedAt: string;
  downloadCount: number;
  downloadUrl: string;
}

export interface Agent {
  agentId: number;
  name: string;
  description: string;
  agentType: string;
  systemPrompt: string;
  configuration: string;
  suggestedPrompts: string[];
  avatarColor: string;
  /** Admin-chosen icon key (see AGENT_ICON_OPTIONS); null = automatic from the name. */
  avatarIcon?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  hasAccess?: boolean;
  // "public" (open to all) | "restricted" (listed for all, usable with a grant)
  // | "per_user" (private: hidden and unusable without a grant)
  visibilityMode?: string;
}

export interface UserAgentAccess {
  userAgentAccessId: number;
  userId: number;
  userName: string;
  userEmail: string;
  agentId: number;
  agentName: string;
  grantedAt: string;
  grantedBy?: string;
}

// ==================== Data Governance Types ====================

export type ColumnType = 'string' | 'number' | 'date' | 'timestamp' | 'boolean';
export type Sensitivity = 'public' | 'internal' | 'confidential' | 'restricted';
/** Ordered from most to least revealing. */
export type ColumnAction = 'show' | 'partial' | 'mask' | 'hide';
export type RuleOperator =
  | 'eq' | 'neq' | 'lt' | 'lte' | 'gt' | 'gte' | 'in' | 'not_in' | 'between'
  | 'contains' | 'starts_with' | 'is_null' | 'not_null';

export interface DatasetColumn {
  name: string;
  label: string;
  type: ColumnType;
  sensitivity: Sensitivity;
}

export interface GovernedDataset {
  id: number;
  /** Name agents use in SQL instead of the Athena table. */
  key: string;
  name: string;
  description: string;
  athenaDatabase: string;
  athenaTable: string;
  columns: DatasetColumn[];
  /** Empty = every agent. */
  agentIds: number[];
  isActive: boolean;
  policyCount: number;
  updatedAt: string;
  problem?: string | null;
}

export type SaveDatasetRequest = Omit<GovernedDataset, 'id' | 'policyCount' | 'updatedAt' | 'problem'>;

export type PolicySubject =
  | { type: 'everyone' }
  | { type: 'user'; id: number }
  | { type: 'department'; id: number }
  | { type: 'role'; value: string }
  | { type: 'attribute'; key: string; value: string };

export interface RuleCondition {
  column: string;
  op: RuleOperator;
  /** A literal, {user.attribute}, or a list for in / not in / between. */
  value?: string | string[];
}

export interface RowRule {
  match: 'all' | 'any';
  conditions: RuleCondition[];
}

export interface ColumnRules {
  default: ColumnAction;
  columns: Record<string, ColumnAction>;
}

export interface DataPolicy {
  id: number;
  datasetId: number;
  datasetName: string;
  name: string;
  description: string;
  appliesTo: PolicySubject[];
  rowRule: RowRule;
  columnRules: ColumnRules;
  isActive: boolean;
  updatedAt: string;
  updatedBy?: string | null;
  rowSummary: string;
  hiddenColumns: string[];
  maskedColumns: string[];
  problem?: string | null;
}

export interface SaveDataPolicyRequest {
  datasetId: number;
  name: string;
  description: string;
  appliesTo: PolicySubject[];
  rowRule: RowRule;
  columnRules: ColumnRules;
  isActive: boolean;
}

export interface UserAttributes {
  userId: number;
  fullName: string;
  email: string;
  role: string;
  departmentId?: number | null;
  departmentName?: string | null;
  isActive: boolean;
  attributes: Record<string, string>;
}

export interface UserAttributesOverview {
  keys: string[];
  users: UserAttributes[];
}

export interface ImportAttributesResult {
  usersUpdated: number;
  valuesSet: number;
  unknownEmails: string[];
  errors: string[];
}

export interface ColumnAccess {
  name: string;
  label: string;
  type: ColumnType;
  sensitivity: Sensitivity;
  /** show | partial | mask | hide, or varies when policies differ by row. */
  access: ColumnAction | 'varies';
}

export interface PolicyOutcome {
  policyId: number;
  name: string;
  appliesToUser: boolean;
  applied: boolean;
  reason?: string | null;
}

export interface DatasetPreview {
  id: number;
  key: string;
  name: string;
  allowed: boolean;
  denyReason?: string | null;
  rowScope: string;
  columns: ColumnAccess[];
  policies: PolicyOutcome[];
  sql?: string | null;
}

export interface AccessPreview {
  user: { userId: number; fullName: string; email: string; role: string; isActive: boolean; attributes: Record<string, string> };
  datasets: DatasetPreview[];
}

export interface GovernedQueryResult {
  ok: boolean;
  error?: string | null;
  columns: string[];
  rows: Record<string, string | null>[];
  truncated: boolean;
  datasets: string[];
  notes: string[];
}

export interface DataAccessLogEntry {
  id: number;
  createdAt: string;
  userId: number;
  userName?: string | null;
  userEmail?: string | null;
  agentId?: number | null;
  agentName?: string | null;
  action: 'query' | 'denied' | 'redacted' | 'withheld';
  datasets: string;
  policyIds: string;
  rowsReturned?: number | null;
  detail: string;
}

// ==================== MCP Connector Types ====================

/** MCP transport. `http` is Streamable HTTP; `sse` is the legacy transport. */
export type ConnectorTransport = 'http' | 'sse';

export type ConnectorAuthType = 'none' | 'bearer' | 'oauth';

/**
 * Reachability as of the last probe. `disconnected` means "not verified yet",
 * `error` means the handshake actually failed — the two are shown differently.
 */
export type ConnectorStatus = 'connected' | 'disconnected' | 'error';

/** One tool an MCP server advertises via tools/list. */
export interface McpTool {
  name: string;
  description?: string;
}

/**
 * A connector owned by the signed-in user. Note there is no token field: the
 * API stores credentials encrypted and never returns them, so `hasCredentials`
 * is all the UI can know.
 */
export interface McpConnector {
  mcpConnectorId: number;
  provider: string;
  name: string;
  description: string;
  serverUrl: string;
  transport: ConnectorTransport;
  authType: ConnectorAuthType;
  hasCredentials: boolean;
  /** 'google' when the app runs this connector's sign-in: no URL or token to edit. */
  signIn?: 'google' | null;
  /** The account signed in through {@link signIn}. */
  accountEmail?: string | null;
  status: ConnectorStatus;
  statusDetail?: string | null;
  isEnabled: boolean;
  allowedTools: string[];
  discoveredTools: McpTool[];
  lastCheckedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** A template in the "add connector" picker, not a saved connector. */
export interface ConnectorCatalogEntry {
  provider: string;
  name: string;
  description: string;
  category: string;
  suggestedServerUrl?: string | null;
  transport: ConnectorTransport;
  authType: ConnectorAuthType;
  docsUrl?: string | null;
  requiresServerUrl: boolean;
  setupHint?: string | null;
  /** 'google' when connecting is a sign-in button rather than a form. */
  signIn?: 'google' | null;
}

export interface ConnectorTestResult {
  success: boolean;
  status: ConnectorStatus;
  message?: string | null;
  serverName?: string | null;
  protocolVersion?: string | null;
  tools: McpTool[];
  elapsedMs: number;
}

export interface CreateConnectorPayload {
  provider: string;
  name?: string;
  description?: string;
  serverUrl: string;
  transport?: ConnectorTransport;
  authType?: ConnectorAuthType;
  authToken?: string;
  allowedTools?: string[];
  isEnabled?: boolean;
}

/**
 * Partial update. Omit `authToken` to keep the stored credential; pass an empty
 * string to clear it. Sending `undefined` and `''` must stay distinguishable,
 * otherwise renaming a connector would wipe its token.
 */
export interface UpdateConnectorPayload {
  name?: string;
  description?: string;
  serverUrl?: string;
  transport?: ConnectorTransport;
  authType?: ConnectorAuthType;
  authToken?: string;
  allowedTools?: string[];
  isEnabled?: boolean;
}

export interface ChatHistory {
  sessionId: number;
  sessionIdentifier: string;
  agentId?: number;
  agentName?: string;
  agentType?: string;
  /** Chats a scheduled model task writes into (no agent): the model that answers there. */
  modelId?: string | null;
  modelName?: string | null;
  startedAt: string;
  lastActivityAt: string;
  messages: ChatMessage[];
  totalMessages?: number;
  title?: string;
}

// Transcription API response
export interface TranscriptionResponse {
  success: boolean;
  text?: string;
  error?: string;
  confidence?: number;
}

// Auth APIs
export const authAPI = {
  login: (data: LoginRequest) => api.post<LoginResponse>('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  /** The signed-in user as stored now (role changes apply without a re-login). */
  me: () => api.get<User>('/auth/me'),
  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.post('/auth/change-password', data),
};

// User APIs
export const userAPI = {
  getUsers: (params?: { search?: string; role?: string; isActive?: boolean; departmentId?: number }) =>
    api.get<User[]>('/users', { params }),
  getUser: (id: number) => api.get<User>(`/users/${id}`),
  createUser: (data: any) => api.post<User>('/users', data),
  updateUser: (id: number, data: any) => api.put<User>(`/users/${id}`, data),
  deleteUser: (id: number) => api.delete(`/users/${id}`),
  updateStatus: (id: number, isActive: boolean) =>
    api.patch(`/users/${id}/status`, { isActive }),
  getAgentUsage: (id: number) =>
    api.get<UserAgentUsage[]>(`/users/${id}/agent-usage`),
};

// Department APIs
export const departmentAPI = {
  getDepartments: (params?: { search?: string; isActive?: boolean }) =>
    api.get<Department[]>('/departments', { params }),
  getDepartment: (id: number) => api.get<Department>(`/departments/${id}`),
  getMembers: (id: number) => api.get<User[]>(`/departments/${id}/members`),
  createDepartment: (data: any) => api.post<Department>('/departments', data),
  updateDepartment: (id: number, data: any) => api.put<Department>(`/departments/${id}`, data),
  deleteDepartment: (id: number) => api.delete(`/departments/${id}`),
};

// Analytics APIs (Admin only)
/** One user's or agent's usage. Token counts are estimates (chars / charsPerToken). */
export interface UsageRow {
  id?: number;
  name: string;
  email?: string;
  messages: number;
  agentReplies: number;
  inputTokens: number;
  outputTokens: number;
  activeDays: number;
  /** User messages in the last 24 hours (rolling). */
  last24h: number;
  estimatedCost?: number | null;
}

export interface UsageAlert {
  kind: 'user' | 'agent';
  id?: number;
  name: string;
  last24h: number;
  dailyAvg: number;
  ratio?: number | null;
  reason: 'spike' | 'near-limit';
}

export interface UsageOverview {
  rangeDays: number;
  tzOffsetMinutes: number;
  charsPerToken: number;
  cost: { enabled: boolean; inputPer1kTokens: number; outputPer1kTokens: number; currency: string };
  /** Per-user daily message limit (UTC day); 0 = off. */
  dailyMessageLimit: number;
  totals: { messages: number; agentReplies: number; inputTokens: number; outputTokens: number; estimatedCost?: number | null };
  users: UsageRow[];
  agents: UsageRow[];
  alerts: UsageAlert[];
  generatedAt: string;
}

/** One row of the agents' own S3 cost reports: an agent, a user, a model, or the total. */
export interface AgentCostRow {
  id: string;
  name: string;
  email?: string | null;
  /** Users only: whether the id matched an account in this app. */
  isAppUser?: boolean | null;
  /** Users only: the user's current department. */
  departmentId?: number | null;
  departmentName?: string | null;
  requests: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  costUsd: number;
  avgLatencyMs: number;
  activeDays: number;
  uniqueUsers?: number | null;
  agentCount?: number | null;
  models?: string[] | null;
  lastSeenAt?: string | null;
}

export interface AgentCostDay {
  date: string;
  requests: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}

/** Real tokens and cost the agents report to S3 (UTC days). */
export interface AgentCostOverview {
  available: boolean;
  /** 'not-configured' | 'access-denied' | 'read-failed' when unavailable. */
  message?: string | null;
  rangeDays: number;
  fromDate: string;
  toDate: string;
  currency: string;
  /** USD → SAR rate for the panel's currency switch. */
  sarPerUsd: number;
  totals: AgentCostRow;
  daily: AgentCostDay[];
  agents: AgentCostRow[];
  users: AgentCostRow[];
  models: AgentCostRow[];
  generatedAt: string;
}

/** A model, a scheduled task, or the total, in the schedule cost report. */
export interface ScheduleCostRow {
  /** Model id, or task id. */
  id: string;
  name: string;
  /** Runs answered by this model / task (notify checks excluded). */
  runs: number;
  /** Bedrock requests: every tool-loop turn and notify check. */
  requests: number;
  notifyChecks: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  /** Tokens billed at a model's higher long-context rate. */
  longContextTokens: number;
  costUsd: number;
  /** False when some tokens are on a model with no configured price. */
  priced: boolean;
  activeDays: number;
  // Models
  inputPerMillion?: number | null;
  outputPerMillion?: number | null;
  taskCount?: number | null;
  // Tasks
  ownerId?: number | null;
  ownerName?: string | null;
  ownerEmail?: string | null;
  /** Tasks only: the owner's current department. */
  ownerDepartmentId?: number | null;
  ownerDepartmentName?: string | null;
  modelIds?: string[] | null;
  deleted?: boolean | null;
}

export interface ScheduleCostDay {
  date: string;
  runs: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}

/** Token cost of scheduled tasks that run on a Bedrock model (days in the viewer's time zone). */
export interface ScheduleCostOverview {
  rangeDays: number;
  tzOffsetMinutes: number;
  fromDate: string;
  toDate: string;
  currency: string;
  sarPerUsd: number;
  totals: ScheduleCostRow;
  daily: ScheduleCostDay[];
  models: ScheduleCostRow[];
  tasks: ScheduleCostRow[];
  /** Models with no price in BedrockModels:Pricing; counted as 0. */
  unpricedModels: string[];
  generatedAt: string;
}

export interface FeedbackReviewItem {
  feedbackId: number;
  feedbackType: 'positive' | 'negative';
  createdAt: string;
  updatedAt?: string;
  userId: number;
  userName: string;
  userEmail: string;
  agentId?: number;
  agentName?: string;
  avatarColor?: string;
  messageId: number;
  sessionId: number;
  /** The agent reply that was rated. */
  responseText: string;
  /** The user message that prompted it. */
  promptText?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  reviewNote?: string;
}

export interface FeedbackReviewPage {
  items: FeedbackReviewItem[];
  total: number;
  page: number;
  pageSize: number;
  /** Counts within the date/agent/search scope, ignoring type and status. */
  counts: { positive: number; negative: number; unreviewed: number; unreviewedNegative: number };
}

export interface FeedbackReviewQuery {
  type?: 'positive' | 'negative';
  status?: 'unreviewed' | 'reviewed';
  agentId?: number;
  search?: string;
  days?: number;
  page?: number;
  pageSize?: number;
}

// The browser's UTC offset in minutes east (UTC+3 → 180), so the server buckets
// days and hours on the viewer's clock rather than UTC.
const tzOffsetMinutes = () => -new Date().getTimezoneOffset();

export const analyticsAPI = {
  getOverview: (days: number = 30, opts?: { refresh?: boolean; signal?: AbortSignal }) =>
    api.get<AnalyticsOverview>('/analytics/overview', {
      params: { days, tzOffsetMinutes: tzOffsetMinutes(), refresh: opts?.refresh || undefined },
      signal: opts?.signal,
    }),
  getAgentUsage: (days: number = 30, top: number = 50) =>
    api.get<AgentUsageStat[]>('/analytics/agents', { params: { days, top, tzOffsetMinutes: tzOffsetMinutes() } }),
  getNegativeFeedback: (days: number = 30, limit: number = 50, signal?: AbortSignal) =>
    api.get<NegativeFeedbackItem[]>('/analytics/feedback/negative', {
      params: { days, limit, tzOffsetMinutes: tzOffsetMinutes() },
      signal,
    }),
  getUsage: (days: number = 30, opts?: { refresh?: boolean; signal?: AbortSignal }) =>
    api.get<UsageOverview>('/analytics/usage', {
      params: { days, tzOffsetMinutes: tzOffsetMinutes(), refresh: opts?.refresh || undefined },
      signal: opts?.signal,
    }),
  getAgentCosts: (days: number = 30, opts?: { refresh?: boolean; signal?: AbortSignal }) =>
    api.get<AgentCostOverview>('/analytics/agent-costs', {
      params: { days, refresh: opts?.refresh || undefined },
      signal: opts?.signal,
    }),
  /** `utc`: bucket days in UTC, to line up with the agents' (UTC) cost reports. */
  getScheduleCosts: (days: number = 30, opts?: { refresh?: boolean; signal?: AbortSignal; utc?: boolean }) =>
    api.get<ScheduleCostOverview>('/analytics/schedule-costs', {
      params: { days, tzOffsetMinutes: opts?.utc ? 0 : tzOffsetMinutes(), refresh: opts?.refresh || undefined },
      signal: opts?.signal,
    }),
  getFeedback: (query: FeedbackReviewQuery, signal?: AbortSignal) =>
    api.get<FeedbackReviewPage>('/analytics/feedback', {
      params: { ...query, search: query.search || undefined, tzOffsetMinutes: tzOffsetMinutes() },
      signal,
    }),
  reviewFeedback: (feedbackId: number, reviewed: boolean, note?: string) =>
    api.patch<{ feedbackId: number; reviewedAt?: string; reviewedBy?: string; reviewNote?: string }>(
      `/analytics/feedback/${feedbackId}/review`,
      { reviewed, note }
    ),
};

// Streaming response types
export interface StreamSessionEvent {
  sessionId: number;
  isNew: boolean;
  userMessageId: number;
  resumed?: boolean;
  files?: FileInfo[];
}

export interface StreamChunkEvent {
  text: string;
}

export interface StreamToolEvent {
  event: 'start' | 'end';
  name: string;
}

/**
 * Producer-side progress signal. Emitted while the backend is fetching staged
 * attachments from S3 and running text extraction, before the agent starts
 * streaming answer tokens. The client flips attachment chips into an
 * "analyzing…" state based on these.
 */
export interface StreamStatusEvent {
  /** policy_check: a governed answer is being reviewed against the user's data policies. */
  stage: 'extracting' | 'invoking' | 'policy_check';
  file?: string;
  done?: boolean;
  error?: string;
  extractedChars?: number;
}

export interface StreamDoneEvent {
  agentMessageId: number | null;
  fullText: string | null;
  noStream?: boolean;
}

export interface StreamStatusResponse {
  active: boolean;
  done: boolean;
  chunkCount: number;
  agentMessageId?: number;
  userMessageId?: number;
  errorMessage?: string;
}

export type StreamEventHandler = {
  onSession?: (data: StreamSessionEvent) => void;
  onChunk?: (data: StreamChunkEvent) => void;
  onTool?: (data: StreamToolEvent) => void;
  onStatus?: (data: StreamStatusEvent) => void;
  onDone?: (data: StreamDoneEvent) => void;
  onError?: (error: Error) => void;
};

/**
 * Read a text/event-stream response and dispatch its events to `handlers`.
 * Resolves true once a terminal event (done or error) was delivered, false if
 * the stream closed without one (connection dropped mid-answer).
 *
 * Parser state lives across network reads: a large event — the final "done"
 * carries the whole answer — routinely spans several reads, and resetting
 * between them silently dropped it, leaving the reply missing until refresh.
 * `typedErrors` keeps the server's error code (send path) instead of a bare
 * message.
 */
async function readEventStream(
  response: Response,
  handlers: StreamEventHandler,
  { typedErrors = false }: { typedErrors?: boolean } = {},
): Promise<boolean> {
  const reader = response.body?.getReader();
  if (!reader) throw new Error('No reader available');
  const decoder = new TextDecoder();
  let buffer = '';
  let event = '';
  let data: string[] = [];

  const dispatch = (): boolean => {
    const name = event || 'message';
    const raw = data.join('\n');
    event = '';
    data = [];
    if (!raw) return false;
    let payload: any;
    try {
      payload = JSON.parse(raw);
    } catch (e) {
      console.error('Failed to parse SSE data:', e);
      return false;
    }
    switch (name) {
      case 'session': handlers.onSession?.(payload as StreamSessionEvent); return false;
      case 'chunk': handlers.onChunk?.(payload as StreamChunkEvent); return false;
      case 'tool': handlers.onTool?.(payload as StreamToolEvent); return false;
      case 'status': handlers.onStatus?.(payload as StreamStatusEvent); return false;
      case 'done':
        handlers.onDone?.(payload as StreamDoneEvent);
        // A reply spends credits; let the sidebar meter refresh.
        notifyCreditsChanged();
        return true;
      case 'error':
        handlers.onError?.(typedErrors || payload?.code
          ? new InlineError((payload?.code as InlineErrorCode) ?? 'UNKNOWN', payload?.message)
          : new Error(payload?.message));
        return true;
      default: return false;
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    buffer += done ? decoder.decode() : decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = done ? '' : lines.pop() ?? '';
    for (const rawLine of lines) {
      const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;
      if (line === '') {
        if (dispatch()) {
          reader.cancel().catch(() => {});
          return true;
        }
      } else if (line.startsWith(':')) {
        // comment / heartbeat
      } else if (line.startsWith('event:')) {
        event = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        data.push(line.slice(5).replace(/^ /, ''));
      }
    }
    if (done) return data.length > 0 ? dispatch() : false;
  }
}

export interface StagedAttachmentRef {
  s3Key: string;
  fileName: string;
  fileType: string;
  sizeBytes: number;
}

// Agent APIs
export const agentAPI = {
  getAgents: (isActive?: boolean) =>
    api.get<Agent[]>('/agents', { params: { isActive } }),
  getMyAgents: () =>
    api.get<Agent[]>('/agents/my-agents'),
  getAgent: (id: number) =>
    api.get<Agent>(`/agents/${id}`),
  createAgent: (data: any) =>
    api.post<Agent>('/agents', data),
  updateAgent: (id: number, data: any) =>
    api.put(`/agents/${id}`, data),
  deleteAgent: (id: number) =>
    api.delete(`/agents/${id}`),
  grantAccess: (userId: number, agentId: number) =>
    api.post('/agents/grant-access', { userId, agentId }),
  revokeAccess: (userId: number, agentId: number) =>
    api.delete(`/agents/revoke-access/${userId}/${agentId}`),
  getAgentUsers: (agentId: number) =>
    api.get<UserAgentAccess[]>(`/agents/${agentId}/users`),
  getUserAgents: (userId: number) =>
    api.get<UserAgentAccess[]>(`/agents/user/${userId}/agents`),
  getAgentDepartments: (agentId: number) =>
    api.get<DepartmentAgentAccess[]>(`/agents/${agentId}/departments`),
  grantDepartmentAccess: (departmentId: number, agentId: number) =>
    api.post('/agents/grant-department', { departmentId, agentId }),
  revokeDepartmentAccess: (departmentId: number, agentId: number) =>
    api.delete(`/agents/revoke-department/${departmentId}/${agentId}`),
};

/** A whole department's grant to one agent. Live: covers current and future members. */
export interface DepartmentAgentAccess {
  departmentAgentAccessId: number;
  departmentId: number;
  departmentName: string;
  /** Inactive departments' grants are kept but don't give access. */
  departmentIsActive: boolean;
  memberCount: number;
  agentId: number;
  grantedAt: string;
  grantedBy?: string | null;
}

// ==================== Scheduled Tasks ====================

export type TaskScheduleType = 'once' | 'hourly' | 'daily' | 'weekly' | 'monthly';
export type TaskRunStatus = 'succeeded' | 'failed' | 'skipped';
/** When a task emails its result. "changed" = only when the answer differs from last time (and on failure). */
export type TaskNotifyOn = 'none' | 'always' | 'success' | 'failure' | 'changed' | 'condition';
export type TaskOutputFormat = 'docx' | 'xlsx' | 'csv' | 'md' | 'html';

export interface TaskRecipientUser {
  userId: number;
  fullName: string;
  email: string;
  department: string | null;
}

export interface TaskRecipients {
  /** The task owner. */
  self: boolean;
  users: TaskRecipientUser[];
  /** Free-typed addresses (only in the server's allowed domains). */
  emails: string[];
  /** Set when each recipient gets their own report. */
  personalize?: TaskPersonalization | null;
}

/**
 * Per-recipient prompt values: each recipient's report is written with their
 * own values, e.g. {{cost_center}} = 2342 for one person and 3844 for another.
 * Keys of `values`: 'self', `user:${id}` or `email:${lower-case address}`.
 */
export interface TaskPersonalization {
  fields: string[];
  values: Record<string, Record<string, string>>;
}

export interface ScheduledTask {
  scheduledTaskId: number;
  title: string;
  prompt: string;
  /** Agent tasks; null when the task runs on a Bedrock model. */
  agentId: number | null;
  /** Agent name, or the model id for model tasks. */
  agentName: string;
  /** Bedrock model / inference-profile id for model tasks. */
  modelId: string | null;
  systemPrompt: string | null;
  temperature: number | null;
  maxTokens: number | null;
  /** Model tasks: web search + page reading. */
  allowWeb: boolean;
  /** Model tasks: the owner's MCP connectors. */
  allowConnectors: boolean;
  /** Deliver the result as a file; null = text only. */
  outputFormat: TaskOutputFormat | null;
  scheduleType: TaskScheduleType;
  /** "HH:mm" in timeZone; unused for once. Hourly: the first run of each day. */
  timeOfDay: string;
  /** Hourly: hours between runs (1–12). */
  intervalHours: number | null;
  /** Weekly: bit 0 = Sunday … bit 6 = Saturday. Hourly: optional day filter (0 = every day). */
  daysOfWeek: number;
  dayOfMonth: number | null;
  /** Once: ISO UTC. */
  runAtUtc: string | null;
  timeZone: string;
  isActive: boolean;
  /** Null when paused or nothing is left to run. */
  nextRunAt: string | null;
  lastRunAt: string | null;
  lastStatus: TaskRunStatus | null;
  lastError: string | null;
  /** The chat this task writes its results into. */
  sessionId: number | null;
  isRunning: boolean;
  notifyOn: TaskNotifyOn;
  /** notifyOn = 'condition': the plain-language rule. */
  notifyCondition: string | null;
  recipients: TaskRecipients;
  createdAt: string;
}

export interface SaveScheduledTaskRequest {
  title: string;
  prompt: string;
  /** Exactly one of agentId / modelId. */
  agentId: number | null;
  modelId: string | null;
  systemPrompt: string | null;
  temperature: number | null;
  maxTokens: number | null;
  /** Model tasks: web search + page reading. */
  allowWeb: boolean;
  /** Model tasks: the owner's MCP connectors. */
  allowConnectors: boolean;
  /** Deliver the result as a file; null = text only. */
  outputFormat: TaskOutputFormat | null;
  scheduleType: TaskScheduleType;
  timeOfDay: string;
  intervalHours: number | null;
  daysOfWeek: number;
  dayOfMonth: number | null;
  runAtUtc: string | null;
  timeZone: string;
  notifyOn: TaskNotifyOn;
  /** notifyOn = 'condition': the plain-language rule. */
  notifyCondition: string | null;
  recipients: { self: boolean; userIds: number[]; emails: string[]; personalize?: TaskPersonalization | null } | null;
}

export interface ScheduledTaskRun {
  scheduledTaskRunId: number;
  startedAt: string;
  finishedAt: string | null;
  status: TaskRunStatus;
  error: string | null;
  messageId: number | null;
  manual: boolean;
  /** First characters of the answer. */
  preview: string | null;
  /** Real token counts — model tasks only. */
  inputTokens: number | null;
  outputTokens: number | null;
  emailStatus: 'sent' | 'failed' | null;
  emailCount: number;
  steps: TaskRunStep[];
  /** Why the result was (not) emailed, when a model judged it. */
  notifyReason: string | null;
  /** Personalized tasks: whose report this run was. */
  recipient?: string | null;
  files: FileInfo[];
}

/** One thing a run did: a tool call, or a note (e.g. a connector that failed to connect). */
export interface TaskRunStep {
  kind: 'tool' | 'note';
  name: string;
  input: string | null;
  ok: boolean;
  output: string | null;
  ms: number;
}

/** What a plain-language request turned into (POST /tasks/draft). Nothing is saved yet. */
export interface TaskDraft {
  kind: 'create' | 'manage' | 'none';
  summary: string;
  request: SaveScheduledTaskRequest | null;
  upcoming: string[];
  action: 'pause' | 'resume' | 'run' | 'delete' | null;
  /** What the draft couldn't honour, e.g. email is off or an address isn't allowed. */
  warnings: string[];
  /** Directory users behind request.recipients.userIds. */
  recipientUsers: TaskRecipientUser[];
  taskId: number | null;
  taskTitle: string | null;
}

export interface TaskOptions {
  emailEnabled: boolean;
  maxRecipients: number;
  /** Domains free-typed recipients may use; empty = directory users only. */
  allowedRecipientDomains: string[];
  modelsEnabled: boolean;
  defaultMaxTokens: number;
  maxTokensCap: number;
  maxPromptLength: number;
  webSearchEnabled: boolean;
  /** An admin gave the caller connectors at all. */
  connectorsEnabled: boolean;
  /** The caller's enabled connectors a model task could use. */
  connectors: string[];
  outputFormats: TaskOutputFormat[];
  variables: { name: string; description: string }[];
}

export interface TaskModel {
  id: string;
  name: string;
  provider: string;
  /** "profile" = cross-region inference profile. */
  kind: 'model' | 'profile';
}

export interface TaskAccess {
  enabled: boolean;
  maxActiveTasks: number;
  activeCount: number;
}

export interface AdminTaskAccess {
  userId: number;
  fullName: string;
  email: string;
  role: string;
  tasksEnabled: boolean;
  /** The user's own cap; null = the configured default. */
  maxActiveTasks: number | null;
  effectiveMaxActiveTasks: number;
  activeCount: number;
  totalCount: number;
}


/** An admin-published starting point for a scheduled task. */
export interface TaskTemplate {
  taskTemplateId: number;
  /** Seeded with the app: editable and hideable, not deletable. */
  builtIn: boolean;
  title: string;
  titleAr: string | null;
  description: string;
  descriptionAr: string | null;
  /** An emoji. */
  icon: string;
  sortOrder: number;
  isPublished: boolean;
  /** Fills the editor. For users, an open engine is already resolved to the default model. */
  request: SaveScheduledTaskRequest;
  agentName: string | null;
}

export interface SaveTaskTemplateRequest {
  title: string;
  titleAr: string | null;
  description: string;
  descriptionAr: string | null;
  icon: string;
  sortOrder: number;
  isPublished: boolean;
  request: SaveScheduledTaskRequest;
}

export const taskAPI = {
  getAccess: () => api.get<TaskAccess>('/tasks/access'),
  options: () => api.get<TaskOptions>('/tasks/options'),
  models: () => api.get<TaskModel[]>('/tasks/models'),
  searchRecipients: (q: string) => api.get<TaskRecipientUser[]>('/tasks/recipients', { params: { q } }),
  preview: (data: SaveScheduledTaskRequest) => api.post<{ runs: string[]; error: string | null }>('/tasks/preview', data),
  testEmail: () => api.post<{ message: string }>('/tasks/email/test'),
  draft: (text: string, timeZone: string) => api.post<TaskDraft>('/tasks/draft', { text, timeZone }),
  list: () => api.get<ScheduledTask[]>('/tasks'),
  create: (data: SaveScheduledTaskRequest) => api.post<ScheduledTask>('/tasks', data),
  update: (id: number, data: SaveScheduledTaskRequest) => api.put<ScheduledTask>(`/tasks/${id}`, data),
  remove: (id: number) => api.delete(`/tasks/${id}`),
  pause: (id: number) => api.post<ScheduledTask>(`/tasks/${id}/pause`),
  resume: (id: number) => api.post<ScheduledTask>(`/tasks/${id}/resume`),
  runNow: (id: number) => api.post(`/tasks/${id}/run`),
  runs: (id: number, take = 20) => api.get<ScheduledTaskRun[]>(`/tasks/${id}/runs`, { params: { take } }),
  templates: () => api.get<TaskTemplate[]>('/tasks/templates'),

  // Admin
  adminListAccess: () => api.get<AdminTaskAccess[]>('/tasks/admin/access'),
  adminUpdateAccess: (userId: number, data: { enabled: boolean; maxActiveTasks: number | null }) =>
    api.put<{ message: string; pausedTasks: number }>(`/tasks/admin/access/${userId}`, data),
  adminListTemplates: () => api.get<TaskTemplate[]>('/tasks/admin/templates'),
  adminCreateTemplate: (data: SaveTaskTemplateRequest) => api.post<TaskTemplate>('/tasks/admin/templates', data),
  adminUpdateTemplate: (id: number, data: SaveTaskTemplateRequest) => api.put<TaskTemplate>(`/tasks/admin/templates/${id}`, data),
  adminDeleteTemplate: (id: number) => api.delete(`/tasks/admin/templates/${id}`),
};

/** The company's n8n editor. */
export interface WorkflowAccess {
  /** The server has a usable Workflows:EditorUrl. */
  configured: boolean;
  /** Configured, and this user may open it (admin-granted; admins always). */
  enabled: boolean;
  editorUrl: string | null;
  /** n8n allows being framed on this site; otherwise the page opens it in its own tab. */
  embeddable: boolean;
  /** The user's n8n account, when the server manages accounts. */
  account: 'active' | 'pending' | 'unknown';
  /** Pending only: the link that finishes setting up the n8n account. */
  inviteUrl: string | null;
  /** Admins only (null for everyone else): why the editor isn't embedded. */
  embedIssue?: 'no-site-origin' | 'mixed-content' | 'unreachable' | 'frame-blocked' | null;
  /** Admins only: this site's origin, the one n8n's frame-ancestors must allow. */
  siteOrigin?: string | null;
}

/** What can stop a workflow from being used in chats; each is fixed in n8n. */
export type WorkflowProblem =
  | 'chat-not-public' | 'webhook-not-post' | 'webhook-responds-immediately'
  | 'path-has-parameters' | 'trigger-disabled' | 'no-trigger';

/** An n8n workflow with a chat or webhook trigger, as offered for publishing. */
export interface PublishableWorkflow {
  workflowId: string;
  name: string;
  description: string | null;
  /** Live in n8n; publishing a workflow that isn't also makes it live. */
  active: boolean;
  trigger: 'chat' | 'webhook';
  url: string | null;
  needsBasicAuth: boolean;
  problems: WorkflowProblem[];
  publishedAgentId: number | null;
  publishedAgentName: string | null;
}

export interface PublishWorkflowRequest {
  workflowId: string;
  name: string;
  description: string | null;
  visibilityMode: 'public' | 'restricted' | 'per_user';
  username: string | null;
  password: string | null;
}

export const workflowAPI = {
  getAccess: () => api.get<WorkflowAccess>('/workflows/access'),
  adminListAccess: () => api.get<{
    canManageAccounts: boolean;
    users: { userId: number; workflowsEnabled: boolean; invitePending: boolean }[];
  }>('/workflows/admin/access'),
  adminUpdateAccess: (userId: number, enabled: boolean) =>
    api.put<{ message: string; account: 'active' | 'pending' | 'unknown'; warning: string | null }>(
      `/workflows/admin/access/${userId}`, { enabled }),
  adminListPublishable: () => api.get<PublishableWorkflow[]>('/workflows/admin/publishable'),
  adminPublish: (data: PublishWorkflowRequest) =>
    api.post<{ agentId: number; name: string; activated: boolean }>('/workflows/admin/publish', data),
};

// Data governance (admin): governed datasets, row/column policies, user
// attributes, "test as user" and the access log.
export const governanceAPI = {
  getDatasets: () => api.get<GovernedDataset[]>('/governance/datasets'),
  createDataset: (data: SaveDatasetRequest) => api.post<GovernedDataset>('/governance/datasets', data),
  updateDataset: (id: number, data: SaveDatasetRequest) => api.put<GovernedDataset>(`/governance/datasets/${id}`, data),
  deleteDataset: (id: number) => api.delete(`/governance/datasets/${id}`),
  /** Reads the table's columns from the Glue catalog. */
  loadColumns: (athenaDatabase: string, athenaTable: string) =>
    api.post<DatasetColumn[]>('/governance/datasets/columns', { athenaDatabase, athenaTable }),

  getPolicies: (datasetId?: number) => api.get<DataPolicy[]>('/governance/policies', { params: { datasetId } }),
  createPolicy: (data: SaveDataPolicyRequest) => api.post<DataPolicy>('/governance/policies', data),
  updatePolicy: (id: number, data: SaveDataPolicyRequest) => api.put<DataPolicy>(`/governance/policies/${id}`, data),
  deletePolicy: (id: number) => api.delete(`/governance/policies/${id}`),

  getAttributes: () => api.get<UserAttributesOverview>('/governance/attributes'),
  saveAttributes: (userId: number, attributes: Record<string, string>) =>
    api.put(`/governance/attributes/${userId}`, { attributes }),
  importAttributes: (csv: string) => api.post<ImportAttributesResult>('/governance/attributes/import', { csv }),

  preview: (userId: number, agentId?: number) =>
    api.get<AccessPreview>('/governance/preview', { params: { userId, agentId } }),
  previewQuery: (userId: number, sql: string, agentId?: number) =>
    api.post<GovernedQueryResult>('/governance/preview/query', { userId, sql, agentId }),
  getLogs: (params?: { userId?: number; action?: string; take?: number }) =>
    api.get<DataAccessLogEntry[]>('/governance/logs', { params }),
};

/** Whether the signed-in user may use connectors (admin-granted per account). */
export interface ConnectorAccess {
  enabled: boolean;
}

export interface AdminConnectorAccess {
  userId: number;
  connectorsEnabled: boolean;
  /** Connectors the account has saved, used or not. */
  connectorCount: number;
}

// MCP Connector APIs — everything is scoped to the signed-in user server-side,
// so none of these take a userId (except the admin access endpoints).
export const connectorAPI = {
  getAccess: () => api.get<ConnectorAccess>('/connectors/access'),

  // Admin: who may use connectors. For another admin, "off" hides them.
  adminListAccess: () => api.get<AdminConnectorAccess[]>('/connectors/admin/access'),
  adminUpdateAccess: (userId: number, enabled: boolean) =>
    api.put<{ message: string }>(`/connectors/admin/access/${userId}`, { enabled }),

  /** Starts "Sign in with Google"; navigate the browser to the returned URL. */
  startGoogleSignIn: () => api.post<{ url: string }>('/connectors/google/start'),

  /** Templates for the add-connector picker (Google Drive, OneDrive, …). */
  getCatalog: () =>
    api.get<ConnectorCatalogEntry[]>('/connectors/catalog'),

  getConnectors: () =>
    api.get<McpConnector[]>('/connectors'),

  getConnector: (id: number) =>
    api.get<McpConnector>(`/connectors/${id}`),

  /** Creates the connector, then probes it — the response carries a real status. */
  createConnector: (data: CreateConnectorPayload) =>
    api.post<McpConnector>('/connectors', data),

  updateConnector: (id: number, data: UpdateConnectorPayload) =>
    api.put<McpConnector>(`/connectors/${id}`, data),

  /** Cheap on/off used by the composer toggle. */
  toggleConnector: (id: number, isEnabled: boolean) =>
    api.patch<McpConnector>(`/connectors/${id}/toggle`, { isEnabled }),

  /** Re-runs the MCP handshake. Slow by nature — show a spinner. */
  testConnector: (id: number) =>
    api.post<ConnectorTestResult>(`/connectors/${id}/test`, undefined, { timeout: 40000 }),

  deleteConnector: (id: number) =>
    api.delete(`/connectors/${id}`),
};

// Monthly credits (1 credit = tokensPerCredit tokens across all agents and tasks).
export interface CreditStatus {
  /** Off: nothing is blocked and the meter is hidden. */
  enabled: boolean;
  unlimited: boolean;
  /** 'admin' (never blocked), 'user' (set by an admin) or null. */
  unlimitedReason: 'admin' | 'user' | null;
  /** The allowance in effect. */
  monthlyCredits: number;
  /** The user's own allowance; null = the default. */
  customMonthlyCredits: number | null;
  defaultMonthlyCredits: number;
  bonusCredits: number;
  totalCredits: number;
  usedTokens: number;
  usedCredits: number;
  remainingCredits: number;
  percentUsed: number;
  warning: boolean;
  exhausted: boolean;
  /** "yyyy-MM" in timeZone. */
  period: string;
  /** Start of next month, UTC ISO. */
  resetsAt: string;
  timeZone: string;
  tokensPerCredit: number;
  warnAtPercent: number;
}

export interface AdminUserCredits extends CreditStatus {
  userId: number;
  fullName: string;
  email: string;
  role: string;
  isActive: boolean;
}

export interface CreditGrant {
  creditGrantId: number;
  period: string;
  credits: number;
  reason: string | null;
  grantedBy: string | null;
  createdAt: string;
}

export interface AdminUserCreditDetail {
  status: AdminUserCredits;
  grants: CreditGrant[];
}

export const CREDITS_CHANGED_EVENT = 'credits:changed';

/** Tells listeners (the sidebar meter) that the user's credits may have changed. */
export const notifyCreditsChanged = () => {
  try { window.dispatchEvent(new Event(CREDITS_CHANGED_EVENT)); } catch { /* non-browser */ }
};

export const creditAPI = {
  getMine: () => api.get<CreditStatus>('/credits/me'),
  adminList: () => api.get<AdminUserCredits[]>('/credits/admin/users'),
  adminGet: (userId: number) => api.get<AdminUserCreditDetail>(`/credits/admin/users/${userId}`),
  adminUpdate: (userId: number, data: { monthlyCredits: number | null; unlimited: boolean }) =>
    api.put<AdminUserCreditDetail>(`/credits/admin/users/${userId}`, data),
  adminGrant: (userId: number, data: { credits: number; reason?: string }) =>
    api.post<AdminUserCreditDetail>(`/credits/admin/users/${userId}/grants`, data),
};

// Chat APIs
export interface MessageSearchResult {
  sessionId: number;
  title: string;
  agentId?: number;
  agentName?: string;
  lastActivityAt: string;
  /** First matching message, to scroll to. */
  messageId: number;
  senderType: 'User' | 'Agent';
  /** Text around the first match ("…" where trimmed). */
  snippet: string;
  matchCount: number;
}

/** Pinning and full-text search over the current user's own chats. */
export const chatOrganizeAPI = {
  getPinned: () => api.get<number[]>('/chat/pinned'),
  setPinned: (sessionId: number, pinned: boolean) =>
    api.patch<{ sessionId: number; isPinned: boolean }>(`/chat/sessions/${sessionId}/pin`, { pinned }),
  search: (q: string, signal?: AbortSignal) =>
    api.get<MessageSearchResult[]>('/chat/search', { params: { q }, signal }),
};

/** A read-only share link for one of the current user's chats. */
export interface ShareLink {
  token: string;
  createdAt: string;
  /** Viewers see messages sent up to this moment. */
  snapshotAt: string;
  viewCount: number;
  messageCount: number;
  /** Messages sent after the snapshot — not visible to viewers until refreshed. */
  newerMessageCount: number;
}

export interface SharedChatView {
  title: string;
  agentName?: string;
  agentType?: string;
  avatarColor?: string;
  avatarIcon?: string | null;
  sharedBy: string;
  snapshotAt: string;
  isOwner: boolean;
  /** Present only when the viewer owns the chat. */
  sessionId?: number;
  messages: {
    senderType: 'User' | 'Agent';
    messageText: string;
    sentAt: string;
    files: { fileName: string; fileType: string; fileSize: number }[];
  }[];
}

export const shareAPI = {
  /** Active link for a session, or null when it isn't shared. */
  getForSession: async (sessionId: number): Promise<ShareLink | null> => {
    const r = await api.get<ShareLink | ''>(`/shares/session/${sessionId}`);
    return r.status === 204 || !r.data ? null : (r.data as ShareLink);
  },
  /** Create the link, or move an existing link's snapshot to now. */
  share: (sessionId: number) => api.post<ShareLink>('/shares', { sessionId }),
  revoke: (sessionId: number) => api.delete(`/shares/session/${sessionId}`),
  view: (token: string) => api.get<SharedChatView>(`/shares/${encodeURIComponent(token)}`),
};

/** The URL a share token opens at. */
export const shareUrl = (token: string) => `${window.location.origin}/shared/${token}`;

export const chatAPI = {
  getChatHistory: (sessionId?: number) =>
    api.get<ChatHistory[]>('/chat/history', { params: { sessionId } }),

  /**
   * Streaming message via Server-Sent Events. Files must already be staged to
   * S3 (see fileAPI.requestStagingUrls / uploadStaging.stageFiles); their
   * descriptors go in stagedAttachments. The request body stays small
   * regardless of file size.
   */
  sendMessageStream: (
    message: string,
    sessionId: number | undefined,
    handlers: StreamEventHandler,
    agentId?: number,
    stagedAttachments?: StagedAttachmentRef[]
  ): (() => void) => {
    const token = localStorage.getItem('token');
    const abortController = new AbortController();
    const streamingUrl = getStreamingApiUrl();

    fetch(`${streamingUrl}/chat/message/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Accept': 'text/event-stream',
      },
      body: JSON.stringify({
        message,
        sessionId,
        agentId,
        stagedAttachments: stagedAttachments && stagedAttachments.length > 0 ? stagedAttachments : undefined,
      }),
      signal: abortController.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          // The daily message limit is refused with a plain JSON 429 before any
          // stream starts. Surface its code so the caller shows the limit
          // message (and, being terminal, doesn't try to resume a stream).
          if (response.status === 429) {
            const body = await response.json().catch(() => null);
            if (body?.code === 'CREDITS_EXHAUSTED') notifyCreditsChanged();
            throw new InlineError(
              (body?.code as InlineErrorCode) ?? 'USAGE_LIMIT',
              body?.message ?? userMessageForCode('USAGE_LIMIT'),
            );
          }
          throw new Error('Stream request failed');
        }

        if (!(await readEventStream(response, handlers, { typedErrors: true }))) {
          handlers.onError?.(new Error('Stream ended without done event'));
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          handlers.onError?.(error);
        }
      });

    return () => abortController.abort();
  },
  
  getSessionMessages: (sessionId: number) =>
    api.get<ChatMessage[]>(`/chat/messages/${sessionId}`),
  deleteMessage: (messageId: number) => api.delete(`/chat/messages/${messageId}`),
  deleteSession: (sessionId: number) => api.delete(`/chat/sessions/${sessionId}`),

  // Whether an agent response is still being generated for this session.
  // Used after a browser refresh to decide whether to reconnect.
  getStreamStatus: (sessionId: number) =>
    api.get<StreamStatusResponse>(`/chat/sessions/${sessionId}/stream-status`),

  // Stop an in-flight agent response ("stop generating"). Signals the backend
  // producer to cancel so generation (and token cost) actually halts.
  cancelStream: (sessionId: number) =>
    api.post<{ cancelled: boolean }>(`/chat/sessions/${sessionId}/cancel`),

  // Reconnect to an in-flight stream. Returns the same SSE event shape as
  // sendMessageStream (session → chunk* → done). Starts from chunk offset `from`.
  resumeStream: (
    sessionId: number,
    from: number,
    handlers: StreamEventHandler
  ): (() => void) => {
    const token = localStorage.getItem('token');
    const abortController = new AbortController();
    const streamingUrl = getStreamingApiUrl();

    fetch(`${streamingUrl}/chat/stream/resume/${sessionId}?from=${from}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'text/event-stream',
      },
      signal: abortController.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error('Resume request failed');
        // Closed without done/error — CloudFront likely dropped the connection
        // mid-stream. Signal an error so the caller can retry.
        if (!(await readEventStream(response, handlers))) {
          handlers.onError?.(new Error('Stream ended without done event'));
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') handlers.onError?.(error);
      });

    return () => abortController.abort();
  },
  
  // Feedback APIs
  submitFeedback: (messageId: number, feedbackType: 'positive' | 'negative' | null) =>
    api.post<FeedbackResponse>(`/chat/messages/${messageId}/feedback`, { feedbackType }),
  getFeedback: (messageId: number) =>
    api.get<FeedbackResponse>(`/chat/messages/${messageId}/feedback`),

  // Edit message and get new agent response (returns SSE stream)
  editMessage: (messageId: number, newText: string, handlers: StreamEventHandler) => {
    const token = localStorage.getItem('token');
    const abortController = new AbortController();
    const streamingUrl = getStreamingApiUrl();

    fetch(`${streamingUrl}/chat/messages/${messageId}/edit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Accept': 'text/event-stream',
      },
      body: JSON.stringify({ newText }),
      signal: abortController.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ code: undefined, message: 'Edit failed' }));
          // Carry the server's code (e.g. AGENT_ACCESS_DENIED on a 403) so the
          // caller can explain the refusal instead of guessing.
          throw new InlineError(
            (errorData.code as InlineErrorCode) ?? 'UNKNOWN',
            errorData.message || `HTTP ${response.status}`,
          );
        }
        // Without this the edit spinner never cleared on a dropped connection.
        if (!(await readEventStream(response, handlers))) {
          handlers.onError?.(new Error('Stream ended without done event'));
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') handlers.onError?.(error);
      });
    return () => abortController.abort();
  },

  // Regenerate agent response (returns SSE stream)
  regenerateMessage: (messageId: number, handlers: StreamEventHandler) => {
    const token = localStorage.getItem('token');
    const abortController = new AbortController();
    const streamingUrl = getStreamingApiUrl();

    fetch(`${streamingUrl}/chat/messages/${messageId}/regenerate`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'text/event-stream',
      },
      signal: abortController.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ code: undefined, message: 'Regenerate failed' }));
          // Carry the server's code (e.g. AGENT_ACCESS_DENIED on a 403) so the
          // caller can explain the refusal instead of guessing.
          throw new InlineError(
            (errorData.code as InlineErrorCode) ?? 'UNKNOWN',
            errorData.message || `HTTP ${response.status}`,
          );
        }
        if (!(await readEventStream(response, handlers))) {
          handlers.onError?.(new Error('Stream ended without done event'));
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') handlers.onError?.(error);
      });
    return () => abortController.abort();
  },

  // Rename session
  renameSession: (sessionId: number, title: string) =>
    api.patch(`/chat/sessions/${sessionId}/title`, { title }),
};

// File APIs
export const fileAPI = {
  uploadFile: (file: File, sessionId?: number, messageId?: number) => {
    const formData = new FormData();
    formData.append('file', file);
    if (sessionId) formData.append('sessionId', sessionId.toString());
    if (messageId) formData.append('messageId', messageId.toString());
    
    return api.post('/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadFile: (fileId: number) => api.get(`/files/download/${fileId}`, { responseType: 'blob' }),
  getFiles: (sessionId?: number) => api.get<FileInfo[]>('/files', { params: { sessionId } }),
  deleteFile: (fileId: number) => api.delete(`/files/${fileId}`),
  getFileMetadata: (fileId: number) => api.get<FileInfo>(`/files/${fileId}/metadata`),
};

// Transcription APIs
export const transcriptionAPI = {
  transcribe: (audioBlob: Blob) => {
    const formData = new FormData();
    formData.append('audioFile', audioBlob, 'recording.webm');
    
    return api.post<TranscriptionResponse>('/transcription', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000, // 60 second timeout for transcription
    });
  },
};

// Announcement APIs (sidebar notices managed in Admin › Announcements)
export type AnnouncementIcon =
  | 'megaphone' | 'sparkles' | 'info' | 'alert' | 'gift' | 'party'
  | 'coins' | 'rocket' | 'calendar' | 'bell' | 'wrench' | 'shield';
export type AnnouncementTone = 'info' | 'success' | 'warning' | 'celebrate';

export interface Announcement {
  announcementId: number;
  title: string;
  titleAr?: string | null;
  body: string;
  bodyAr?: string | null;
  icon: AnnouncementIcon;
  tone: AnnouncementTone;
  /** data:image/… URL, or null. */
  imageData?: string | null;
  /** https URL or an in-app path such as /tasks. */
  linkUrl?: string | null;
  linkLabel?: string | null;
  linkLabelAr?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  dismissible: boolean;
  createdAt: string;
}

export interface AdminAnnouncement extends Announcement {
  isActive: boolean;
  updatedAt: string;
  dismissedCount: number;
  createdBy?: string | null;
}

export interface SaveAnnouncementRequest {
  title: string;
  titleAr?: string | null;
  body: string;
  bodyAr?: string | null;
  icon: AnnouncementIcon;
  tone: AnnouncementTone;
  imageData?: string | null;
  linkUrl?: string | null;
  linkLabel?: string | null;
  linkLabelAr?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  isActive: boolean;
  dismissible: boolean;
  /** On update: show it again to everyone who already closed it. */
  resetDismissals?: boolean;
}

export const ANNOUNCEMENTS_CHANGED_EVENT = 'announcements:changed';

/** Tells the sidebar to refetch (after an admin saves or deletes one). */
export const notifyAnnouncementsChanged = () => {
  try { window.dispatchEvent(new Event(ANNOUNCEMENTS_CHANGED_EVENT)); } catch { /* non-browser */ }
};

export const announcementAPI = {
  getLive: () => api.get<Announcement[]>('/announcements'),
  dismiss: (id: number) => api.post(`/announcements/${id}/dismiss`),
  adminList: () => api.get<AdminAnnouncement[]>('/announcements/admin'),
  adminCreate: (data: SaveAnnouncementRequest) => api.post<AdminAnnouncement>('/announcements/admin', data),
  adminUpdate: (id: number, data: SaveAnnouncementRequest) => api.put<AdminAnnouncement>(`/announcements/admin/${id}`, data),
  adminDelete: (id: number) => api.delete(`/announcements/admin/${id}`),
};

// --- Governed agent platform (docs/architecture.md) ---------------------------
// Agent configs carry no model, routing, endpoint or permission fields: the
// server rejects them. The model that handled a reply comes back read-only.

export interface PlatformAgentConfig {
  schemaVersion: 1;
  name: string;
  instructions: string;
  tools?: string[];
  agents?: string[];
  sharing?: { userIds?: number[]; departmentIds?: number[] };
  memory?: { enabled: boolean; retentionDays?: number };
}

export interface PlatformAgentSummary {
  id: string;
  name: string;
  ownerUserId: number;
  isMine: boolean;
  version: number;
  isDisabled: boolean;
  updatedAt: string;
}

export interface PlatformAgentDetail {
  id: string;
  name: string;
  ownerUserId: number;
  isMine: boolean;
  version: number;
  isDisabled: boolean;
  config: PlatformAgentConfig;
  versions: { version: number; changeNote: string; createdAt: string }[] | null;
}

export interface PlatformRegistryItem {
  id: string;
  kind: string;
  description: string;
  isWeb: boolean;
  sideEffect: boolean;
  requiredEntitlement: string;
  available: boolean;
}

export interface PlatformMe {
  role: 'Consumer' | 'Builder' | 'Admin' | 'PlatformOperator';
  tenantId: number | null;
  entitlements: string[];
  models: string[];
}

export interface PlatformViolation {
  path: string;
  kind: string;
  message: string;
}

export interface PlatformInvokeReply {
  text: string;
  stopReason: string;
  modelsUsed: { agentId: string; model: string; reason: string }[];
  usedWebContent: boolean;
  traceId: string;
}

export interface WebAccessRequestRow {
  webAccessRequestId: number;
  userId: number;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  requestedAt: string;
  decisionNote: string;
}

export interface PlatformEntitlementRow {
  platformEntitlementId: number;
  name: string;
  userId: number | null;
  departmentId: number | null;
  grantedAt: string;
  reason: string;
}

export interface PlatformAgentAdminRow {
  platformAgentId: string;
  ownerUserId: number;
  tenantId: number;
  name: string;
  currentVersion: number;
  isDisabled: boolean;
  exposeWebSafe: boolean;
}

export interface PlatformBudgetRow {
  platformBudgetId: number;
  scope: 'User' | 'Agent' | 'Tenant';
  scopeId: string;
  limitUsd: number | null;
  limitTokens: number | null;
}

export interface PlatformAuditRow {
  platformAuditEventId: number;
  at: string;
  type: string;
  actorUserId: number | null;
  tenantId: number | null;
  agentId: string | null;
  dataJson: string;
}

export interface PlatformUsageRow {
  model: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  marginBandCalls: number;
}

export interface AdminBuilderAccess {
  userId: number;
  /** The admin grant itself (ignored while the user is an Admin). */
  agentBuilderEnabled: boolean;
  /** Effective access: Admins always, users only when granted. */
  hasAccess: boolean;
}

export const agentPlatformAPI = {
  /** Whether an admin gave the signed-in user the Agent Builder. Never 403s. */
  access: () => api.get<{ enabled: boolean }>('/agent-platform/access'),
  me: () => api.get<PlatformMe>('/agent-platform/me'),
  list: () => api.get<PlatformAgentSummary[]>('/agent-platform/agents'),
  get: (id: string) => api.get<PlatformAgentDetail>(`/agent-platform/agents/${encodeURIComponent(id)}`),
  create: (config: PlatformAgentConfig) => api.post<{ id: string; version: number }>('/agent-platform/agents', config),
  update: (id: string, config: PlatformAgentConfig, note?: string) =>
    api.put<{ id: string; version: number }>(`/agent-platform/agents/${encodeURIComponent(id)}`, config, { params: { note } }),
  rollback: (id: string, version: number) =>
    api.post<{ id: string; version: number }>(`/agent-platform/agents/${encodeURIComponent(id)}/rollback/${version}`),
  invoke: (id: string, message: string, conversationId?: string) =>
    api.post<PlatformInvokeReply>(`/agent-platform/agents/${encodeURIComponent(id)}/invoke`, { message, conversationId }),
  registry: () => api.get<PlatformRegistryItem[]>('/agent-platform/registry'),
  webAccess: () => api.get<{ hasWebAccess: boolean; requests: WebAccessRequestRow[] }>('/agent-platform/web-access'),
  requestWebAccess: (reason: string) => api.post<WebAccessRequestRow>('/agent-platform/web-access', { reason }),

  admin: {
    webRequests: (status?: string) => api.get<WebAccessRequestRow[]>('/agent-platform/admin/web-requests', { params: { status } }),
    approve: (id: number, note?: string) => api.post(`/agent-platform/admin/web-requests/${id}/approve`, { note }),
    reject: (id: number, note?: string) => api.post(`/agent-platform/admin/web-requests/${id}/reject`, { note }),
    entitlements: () => api.get<PlatformEntitlementRow[]>('/agent-platform/admin/entitlements'),
    grant: (name: string, target: { userId?: number; departmentId?: number }, reason?: string) =>
      api.post<PlatformEntitlementRow>('/agent-platform/admin/entitlements', { name, ...target, reason }),
    revoke: (id: number) => api.delete(`/agent-platform/admin/entitlements/${id}`),
    agents: () => api.get<PlatformAgentAdminRow[]>('/agent-platform/admin/agents'),
    setDisabled: (id: string, value: boolean) => api.post(`/agent-platform/admin/agents/${encodeURIComponent(id)}/disabled`, { value }),
    setExposeWebSafe: (id: string, value: boolean) =>
      api.post(`/agent-platform/admin/agents/${encodeURIComponent(id)}/expose-web-safe`, { value }),
    builderAccess: () => api.get<AdminBuilderAccess[]>('/agent-platform/admin/users/builder-access'),
    setBuilder: (userId: number, value: boolean) =>
      api.post<AdminBuilderAccess>(`/agent-platform/admin/users/${userId}/builder`, { value }),
    budgets: () => api.get<PlatformBudgetRow[]>('/agent-platform/admin/budgets'),
    setBudget: (b: { scope: string; scopeId: string; limitUsd: number | null; limitTokens: number | null }) =>
      api.put<PlatformBudgetRow>('/agent-platform/admin/budgets', b),
    audit: (type?: string) => api.get<PlatformAuditRow[]>('/agent-platform/admin/audit', { params: { type, take: 300 } }),
    verifyAudit: () => api.get<{ intact: boolean; firstBrokenEventId: number | null }>('/agent-platform/admin/audit/verify'),
    usage: () => api.get<PlatformUsageRow[]>('/agent-platform/admin/usage'),
    webDomains: (departmentId: number) =>
      api.get<{ departmentId: number; include: string[]; exclude: string[] }>(`/agent-platform/admin/web-domains/${departmentId}`),
    setWebDomains: (departmentId: number, include: string[], exclude: string[]) =>
      api.put(`/agent-platform/admin/web-domains/${departmentId}`, { include, exclude }),
  },
};

export default api;

